import React, { useMemo } from "react";
import { Box, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from "@chakra-ui/react";
import { PandasDataFrameProto } from "build-ts/dataframe";
import { buildCellMeta, parsePandasDataJson } from "../utils/pandasTableUtils";

interface PandasTableProps {
  df?: PandasDataFrameProto;
  columnTooltips?: Record<string, React.ReactNode>;
}

/**
 * Renders a PandasDataFrameProto as a formatted table.
 * Uses format_map and column_dtypes for cell-level formatting.
 * Pure component — no state or context access.
 */
const PandasTable: React.FC<PandasTableProps> = ({ df, columnTooltips }) => {
  const { columns, rows } = useMemo(
    () => (df?.data_json ? parsePandasDataJson(df.data_json) : { columns: [], rows: [] }),
    [df?.data_json]
  );

  // Column-level format map: column_name → format_string.
  const columnFormatMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (df?.format_map ?? []).forEach(entry => {
      if (entry.column_name) {
        map[entry.column_name] = entry.format_string ?? "";
      }
    });
    return map;
  }, [df?.format_map]);

  // Row-level format map: rowIndex → { colName → format_string }.
  // The column name is encoded inside the format_string as a Python dict key
  // (e.g. "{'value': '${:,.0f}'}" → col "value", format "${:,.0f}").
  // Populated from entries that carry row_index instead of column_name.
  const rowFormatMap = useMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {};
    (df?.format_map ?? []).forEach(entry => {
      if (entry.row_index !== null && entry.format_string) {
        const idx = Number.parseInt(entry.row_index ?? "");
        if (!Number.isNaN(idx)) {
          map[idx] = entry.format_string ?? "";
        }
      }
    });
    return map;
  }, [df?.format_map]);

  const dtypeMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (df?.column_dtypes ?? []).forEach(entry => {
      if (entry.column_name) {
        map[entry.column_name] = entry.dtype ?? "";
      }
    });
    return map;
  }, [df?.column_dtypes]);

  /**
   * Per-column metadata computed once (not per cell).
   * Delegates all dtype/format logic to buildCellMeta.
   */
  const columnMeta = useMemo(() => {
    return columns?.map(col => {
      const { align, formatter } = buildCellMeta(dtypeMap[col], columnFormatMap[col]);
      return { col, align, formatter };
    });
  }, [columns, dtypeMap, columnFormatMap]);

  /**
   * Per-row-per-column metadata, keyed by rowIndex → colName.
   * Delegates all dtype/format logic to buildCellMeta.
   */
  const rowMeta = useMemo(() => {
    const meta: Record<number, Record<string, { formatter: (v: any) => string; align: "left" | "right" }>> = {};
    Object.entries(rowFormatMap).forEach(([rowIdxStr, fmt]) => {
      const rowIdx = Number(rowIdxStr);
      meta[rowIdx] = {};
      Object.entries(dtypeMap).forEach(([col, dtype]) => {
        meta[rowIdx][col] = buildCellMeta(dtype, fmt);
      });
    });
    return meta;
  }, [rowFormatMap, dtypeMap]);

  if (!df || columns?.length === 0) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No data available.
        </Text>
      </Box>
    );
  }

  const tablePad = { base: 1, md: 2 } as const;

  return (
    <Box w="100%" overflowX="auto" px={tablePad}>
      <Table size="sm" variant="simple" w="100%">
        <Thead bg="gray.50" position="sticky" top={0} zIndex={10} boxShadow="sm">
          <Tr>
            {columnMeta?.map(({ col, align }, colIndex) => {
              const tip = columnTooltips?.[col];
              return (
                <Th
                  key={`${col}-${colIndex}`}
                  fontSize="xs"
                  fontWeight="700"
                  color="gray.900"
                  textTransform="none"
                  letterSpacing="normal"
                  borderColor="gray.200"
                  px={3}
                  py={2}
                  whiteSpace="nowrap"
                  textAlign={align}
                >
                  {tip ? (
                    <Tooltip label={tip} hasArrow placement="bottom" openDelay={100}>
                      <Text
                        as="span"
                        display="inline-block"
                        mb={0}
                        fontSize="inherit"
                        fontWeight="inherit"
                        color="inherit"
                        cursor="default"
                        borderBottom="1px dashed"
                        borderColor="gray.400"
                        _hover={{ borderColor: "purple.400", color: "purple.700" }}
                      >
                        {col}
                      </Text>
                    </Tooltip>
                  ) : (
                    col
                  )}
                </Th>
              );
            })}
          </Tr>
        </Thead>
        <Tbody>
          {rows?.map((row, rowIdx) => (
            <Tr key={rowIdx} _hover={{ bg: "gray.50" }} transition="background 0.1s">
              {columnMeta?.map(({ col, align, formatter }, colIndex) => {
                // Use the row-level formatter when present; fall back to the column formatter.
                const rowCell = rowMeta[rowIdx]?.[col];
                const cellValue = rowCell ? rowCell.formatter(row[col]) : formatter(row[col]);
                const cellAlign = rowCell ? rowCell.align : align;

                return (
                  <Td
                    key={`${col}-${rowIdx}-${colIndex}`}
                    fontSize="0.8rem"
                    color="gray.700"
                    borderColor="gray.100"
                    px={3}
                    py={1.5}
                    whiteSpace="normal"
                    fontWeight="600"
                    textAlign={cellAlign}
                  >
                    <Tooltip label={cellValue} placement="top" hasArrow openDelay={300}>
                      <Text
                        as="span"
                        display="block"
                        mb={0}
                        fontSize="inherit"
                        color="inherit"
                        fontWeight="inherit"
                        textAlign="inherit"
                      >
                        {cellValue}
                      </Text>
                    </Tooltip>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default PandasTable;
